import { z } from "zod";
import { RichDocSchema, richTextFromPlain } from "../richtext";
import { StylePropsSchema, type StyleProps } from "../styles";

/**
 * Block registry — the single source of truth for what blocks exist, what
 * attributes they carry, where they may live in the tree, and their default
 * appearance. The editor palette, inspector, renderer compilers and document
 * validation are all driven from here.
 */

export type BlockCategory = "layout" | "content";

export interface BlockDefinition {
    type: string;
    label: string;
    category: BlockCategory;
    /** Node types this block accepts as children; empty = leaf. */
    allowedChildren: readonly string[];
    attrsSchema: z.ZodTypeAny;
    defaultAttrs: () => Record<string, unknown>;
    defaultStyles: StyleProps;
}

const MERGE_TAG_TEXT = /^[^<>]*$/; // plain text fields; tags like {{contact.first_name}} allowed

const url = z.string().max(2000).default("");

const defs: BlockDefinition[] = [
    {
        type: "root",
        label: "Document",
        category: "layout",
        allowedChildren: ["section"],
        attrsSchema: z.object({}).strict(),
        defaultAttrs: () => ({}),
        defaultStyles: {},
    },
    {
        type: "section",
        label: "Section",
        category: "layout",
        allowedChildren: ["row", "hero", "card"],
        attrsSchema: z
            .object({
                fullWidth: z.boolean().default(false),
            })
            .strict(),
        defaultAttrs: () => ({ fullWidth: false }),
        defaultStyles: {
            backgroundColor: "transparent",
            paddingTop: 0,
            paddingBottom: 0,
        },
    },
    {
        type: "row",
        label: "Row",
        category: "layout",
        allowedChildren: ["column"],
        attrsSchema: z
            .object({
                /** Column width ratios, e.g. [1], [1,1], [1,2]. Must match child count. */
                columnRatios: z.array(z.number().positive()).min(1).max(4).default([1]),
                stackOnMobile: z.boolean().default(true),
            })
            .strict(),
        defaultAttrs: () => ({ columnRatios: [1], stackOnMobile: true }),
        defaultStyles: {
            backgroundColor: "transparent",
            paddingTop: 8,
            paddingBottom: 8,
        },
    },
    {
        type: "column",
        label: "Column",
        category: "layout",
        allowedChildren: [
            "heading",
            "paragraph",
            "image",
            "button",
            "divider",
            "spacer",
            "logo",
            "social",
            "nav",
            "callout",
            "quote",
            "footer",
            "rawHtml",
        ],
        attrsSchema: z.object({}).strict(),
        defaultAttrs: () => ({}),
        defaultStyles: { paddingTop: 0, paddingBottom: 0, paddingLeft: 0, paddingRight: 0 },
    },
    {
        type: "heading",
        label: "Heading",
        category: "content",
        allowedChildren: [],
        attrsSchema: z
            .object({
                level: z.union([z.literal(1), z.literal(2), z.literal(3)]).default(2),
                content: RichDocSchema,
            })
            .strict(),
        defaultAttrs: () => ({ level: 2, content: richTextFromPlain("Heading") }),
        defaultStyles: {
            fontSize: 24,
            fontWeight: "bold",
            color: "#1c2430",
            lineHeight: 1.3,
            textAlign: "left",
            paddingTop: 12,
            paddingBottom: 12,
            paddingLeft: 24,
            paddingRight: 24,
        },
    },
    {
        type: "paragraph",
        label: "Text",
        category: "content",
        allowedChildren: [],
        attrsSchema: z.object({ content: RichDocSchema }).strict(),
        defaultAttrs: () => ({
            content: richTextFromPlain("Write something interesting."),
        }),
        defaultStyles: {
            fontSize: 14,
            color: "#3a4454",
            lineHeight: 1.55,
            textAlign: "left",
            paddingTop: 8,
            paddingBottom: 8,
            paddingLeft: 24,
            paddingRight: 24,
        },
    },
    {
        type: "image",
        label: "Image",
        category: "content",
        allowedChildren: [],
        attrsSchema: z
            .object({
                src: url,
                assetId: z.string().optional(),
                alt: z.string().max(500).regex(MERGE_TAG_TEXT).default(""),
                href: url.optional(),
                fluid: z.boolean().default(true),
            })
            .strict(),
        defaultAttrs: () => ({ src: "", alt: "", fluid: true }),
        defaultStyles: {
            align: "center",
            paddingTop: 8,
            paddingBottom: 8,
            paddingLeft: 24,
            paddingRight: 24,
        },
    },
    {
        type: "button",
        label: "Button",
        category: "content",
        allowedChildren: [],
        attrsSchema: z
            .object({
                text: z.string().max(200).regex(MERGE_TAG_TEXT).default("Click me"),
                href: url,
            })
            .strict(),
        defaultAttrs: () => ({ text: "Click me", href: "" }),
        defaultStyles: {
            backgroundColor: "#2f6fed",
            color: "#ffffff",
            fontSize: 14,
            fontWeight: "bold",
            borderRadius: 4,
            align: "center",
            paddingTop: 12,
            paddingBottom: 12,
            paddingLeft: 24,
            paddingRight: 24,
        },
    },
    {
        type: "divider",
        label: "Divider",
        category: "content",
        allowedChildren: [],
        attrsSchema: z.object({}).strict(),
        defaultAttrs: () => ({}),
        defaultStyles: {
            borderWidth: 1,
            borderStyle: "solid",
            borderColor: "#e2e5ea",
            paddingTop: 12,
            paddingBottom: 12,
            paddingLeft: 24,
            paddingRight: 24,
        },
    },
    {
        type: "spacer",
        label: "Spacer",
        category: "content",
        allowedChildren: [],
        attrsSchema: z.object({}).strict(),
        defaultAttrs: () => ({}),
        defaultStyles: { height: 24 },
    },
    {
        type: "hero",
        label: "Hero",
        category: "layout",
        allowedChildren: ["heading", "paragraph", "button", "spacer"],
        attrsSchema: z
            .object({
                src: url.optional(),
                height: z.number().int().min(80).max(800).default(300),
            })
            .strict(),
        defaultAttrs: () => ({ height: 300 }),
        defaultStyles: {
            backgroundColor: "#2f3a4c",
            paddingTop: 40,
            paddingBottom: 40,
            paddingLeft: 24,
            paddingRight: 24,
        },
    },
    {
        type: "card",
        label: "Card",
        category: "layout",
        allowedChildren: ["heading", "paragraph", "image", "button", "divider", "spacer"],
        attrsSchema: z.object({}).strict(),
        defaultAttrs: () => ({}),
        defaultStyles: {
            backgroundColor: "#ffffff",
            borderRadius: 8,
            borderWidth: 1,
            borderStyle: "solid",
            borderColor: "#e2e5ea",
            paddingTop: 16,
            paddingBottom: 16,
            paddingLeft: 8,
            paddingRight: 8,
        },
    },
    {
        type: "logo",
        label: "Logo",
        category: "content",
        allowedChildren: [],
        attrsSchema: z
            .object({
                src: url,
                alt: z.string().max(300).regex(MERGE_TAG_TEXT).default("Logo"),
                href: url.optional(),
            })
            .strict(),
        defaultAttrs: () => ({ src: "", alt: "Logo" }),
        defaultStyles: {
            align: "center",
            width: 160,
            paddingTop: 16,
            paddingBottom: 16,
            paddingLeft: 24,
            paddingRight: 24,
        },
    },
    {
        type: "social",
        label: "Social links",
        category: "content",
        allowedChildren: [],
        attrsSchema: z
            .object({
                links: z
                    .array(
                        z
                            .object({
                                network: z.enum([
                                    "facebook",
                                    "instagram",
                                    "linkedin",
                                    "x",
                                    "youtube",
                                    "tiktok",
                                    "web",
                                ]),
                                url: url,
                            })
                            .strict(),
                    )
                    .max(10)
                    .default([]),
                iconSize: z.number().int().min(16).max(64).default(28),
            })
            .strict(),
        defaultAttrs: () => ({
            links: [
                { network: "facebook", url: "" },
                { network: "instagram", url: "" },
            ],
            iconSize: 28,
        }),
        defaultStyles: {
            align: "center",
            paddingTop: 12,
            paddingBottom: 12,
            paddingLeft: 24,
            paddingRight: 24,
        },
    },
    {
        type: "nav",
        label: "Menu",
        category: "content",
        allowedChildren: [],
        attrsSchema: z
            .object({
                links: z
                    .array(
                        z
                            .object({
                                label: z.string().max(80).regex(MERGE_TAG_TEXT),
                                href: url,
                            })
                            .strict(),
                    )
                    .max(8)
                    .default([]),
            })
            .strict(),
        defaultAttrs: () => ({
            links: [
                { label: "Home", href: "" },
                { label: "About", href: "" },
                { label: "Contact", href: "" },
            ],
        }),
        defaultStyles: {
            fontSize: 13,
            color: "#3a4454",
            textAlign: "center",
            paddingTop: 12,
            paddingBottom: 12,
            paddingLeft: 24,
            paddingRight: 24,
        },
    },
    {
        type: "callout",
        label: "Callout",
        category: "content",
        allowedChildren: [],
        attrsSchema: z.object({ content: RichDocSchema }).strict(),
        defaultAttrs: () => ({
            content: richTextFromPlain("Something worth highlighting."),
        }),
        defaultStyles: {
            backgroundColor: "#eef4ff",
            color: "#1c2430",
            fontSize: 14,
            lineHeight: 1.55,
            borderRadius: 6,
            paddingTop: 16,
            paddingBottom: 16,
            paddingLeft: 24,
            paddingRight: 24,
        },
    },
    {
        type: "quote",
        label: "Quote",
        category: "content",
        allowedChildren: [],
        attrsSchema: z
            .object({
                content: RichDocSchema,
                attribution: z.string().max(200).regex(MERGE_TAG_TEXT).default(""),
            })
            .strict(),
        defaultAttrs: () => ({
            content: richTextFromPlain("A memorable quote goes here."),
            attribution: "",
        }),
        defaultStyles: {
            color: "#3a4454",
            fontSize: 16,
            lineHeight: 1.6,
            borderColor: "#2f6fed",
            paddingTop: 12,
            paddingBottom: 12,
            paddingLeft: 24,
            paddingRight: 24,
        },
    },
    {
        type: "footer",
        label: "Footer",
        category: "content",
        allowedChildren: [],
        attrsSchema: z
            .object({
                content: RichDocSchema,
                includeUnsubscribe: z.boolean().default(true),
                unsubscribeLabel: z.string().max(100).default("Unsubscribe"),
            })
            .strict(),
        defaultAttrs: () => ({
            content: richTextFromPlain("© Your company · 123 Street, City"),
            includeUnsubscribe: true,
            unsubscribeLabel: "Unsubscribe",
        }),
        defaultStyles: {
            fontSize: 12,
            color: "#8a93a2",
            lineHeight: 1.5,
            textAlign: "center",
            paddingTop: 16,
            paddingBottom: 16,
            paddingLeft: 24,
            paddingRight: 24,
        },
    },
    {
        type: "rawHtml",
        label: "HTML",
        category: "content",
        allowedChildren: [],
        attrsSchema: z
            .object({
                html: z.string().max(50_000).default(""),
            })
            .strict(),
        defaultAttrs: () => ({ html: "<p>Raw HTML block</p>" }),
        defaultStyles: {},
    },
];

export const BLOCK_REGISTRY: ReadonlyMap<string, BlockDefinition> = new Map(
    defs.map((d) => [d.type, d]),
);

export function blockDef(type: string): BlockDefinition {
    const def = BLOCK_REGISTRY.get(type);
    if (!def) throw new Error(`Unknown block type: ${type}`);
    return def;
}

export function canContain(parentType: string, childType: string): boolean {
    return blockDef(parentType).allowedChildren.includes(childType);
}

/** Content block types insertable from the editor palette. */
export function paletteBlocks(): BlockDefinition[] {
    return [...BLOCK_REGISTRY.values()].filter(
        (d) => d.category === "content" || d.type === "section" || d.type === "row",
    );
}

// Re-exported so the style layer can validate per-type overrides.
export { StylePropsSchema };
